import React, { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  ContainerSection,
  MAutocomplete,
  MButton,
  MDatePicker,
  ResponsiveFormContainer,
} from "@app/components";
import { IFilterCalibrationAnalysis } from "@app/types/calibration.types";
import {
  useAnalysisTypes,
  useEquipment,
  useEfficiencyParts,
} from "@services/servicesGenericsTables";
import { useCalibrationTritium } from "../../utils/useCalibration";

const FilterCalibrationAnalysis = () => {
  const form = useForm<IFilterCalibrationAnalysis>({ mode: "all" });
  const { equipmentList } = useEquipment();
  const { analysisTypeList } = useAnalysisTypes();
  const { efficiencyPartsList } = useEfficiencyParts();
  const { setQuery } = useCalibrationTritium();

  const analysisWatch = form.watch("analysis_type");

  useEffect(() => {
    // Limpiar el campo part cuando cambian los tipos de análisis
    form.setValue("part", "");
  }, [analysisWatch]);

  // Ordenar tipos de análisis alfabéticamente por descripción
  const sortedAnalysisTypeList = useMemo(() => {
    return [...analysisTypeList].sort((a, b) => 
      a.descripcion.localeCompare(b.descripcion, 'es', { sensitivity: 'base' })
    );
  }, [analysisTypeList]);

  const submit = (data: IFilterCalibrationAnalysis) => {
    setQuery(data);
  };

  const clearFilters = () => {
    form.reset();
    setQuery({});
  };

  return (
    <ContainerSection label="Filtros">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submit)}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ResponsiveFormContainer columns={4}>
              <MAutocomplete
                key={JSON.stringify(form.getValues("analysis_type"))}
                name="analysis_type"
                options={sortedAnalysisTypeList}
                label="Seleccione tipos de analisis"
                getOptionLabel={(option) => `${option.descripcion}` || ""}
                multiple={true}
              />
              <MAutocomplete
                key={JSON.stringify(form.getValues("equipment"))}
                name="equipment"
                options={equipmentList}
                label="Equipo/s"
                getOptionLabel={(option) =>
                  `${option.codigo} - ${option.descripcion}` || ""
                }
                multiple
              />
              <MAutocomplete
                key={`${JSON.stringify(analysisWatch)}`}
                name="part"
                options={
                  analysisWatch && analysisWatch.length > 0
                    ? efficiencyPartsList.filter((part) => {
                        // Incluir partes de cualquiera de los análisis seleccionados
                        return analysisWatch.some(analysis => part.analisis === analysis.codigo);
                      })
                    : []
                }
                label="Parte"
                getOptionLabel={(option) => option.parte || ""}
                helperText={!analysisWatch || analysisWatch.length === 0 ? "Seleccione un análisis" : ""}
                disabled={!analysisWatch || analysisWatch.length === 0}
              />
              <MDatePicker
                name="date_from"
                pickerType="date"
                label="Fecha desde"
              />
              <MDatePicker
                name="date_to"
                pickerType="date"
                label="Fecha hasta"
              />
            </ResponsiveFormContainer>
            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
              <MButton
                variant="outlined"
                color="primary"
                sx={{ textTransform: "uppercase" }}
                text="Filtrar"
                type="submit"
                startIcon={<FilterAltIcon />}
              />
              <MButton
                variant="outlined"
                color="secondary"
                sx={{ textTransform: "uppercase", ml: 2 }}
                text="Limpiar"
                onClick={clearFilters}
              />
            </Box>
          </Box>
        </form>
      </FormProvider>
    </ContainerSection>
  );
};

export default FilterCalibrationAnalysis;
